/**
 * Notifications page — per docs/specs/notifications.md
 *
 * Delivery stats per channel, template performance, failures/bounces,
 * broadcast queue.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v3
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Chip } from '../components/chip';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import {
    cancelBroadcast,
    pauseNotificationChannel,
    resumeNotificationChannel,
    sendBroadcast,
    useBroadcasts,
    useChannelDelivery,
    useNotificationFailures,
    useNotificationKpis,
    useTemplates
} from '../../lib/api';
import { apiPost, getHttpErrorMessage } from '../../lib/http';
import type { BroadcastRow, NotificationFailure, TemplatePerformance } from '../../lib/types';
import { BroadcastModal, TemplateEditorModal } from './notifications-modals';

const broadcastTone = (s: BroadcastRow['state']) =>
    s === 'sending' ? 'info' : s === 'complete' ? 'success' : s === 'queued' ? 'muted' : 'warning';

export function NotificationsPage() {
    const kpis = useNotificationKpis();
    const channels = useChannelDelivery();
    const templates = useTemplates();
    const failures = useNotificationFailures();
    const broadcasts = useBroadcasts();
    const qc = useQueryClient();
    const { toast } = useToast();
    const [activeTemplate, setActiveTemplate] = useState<TemplatePerformance | null>(null);
    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [retryingId, setRetryingId] = useState<string | null>(null);
    const [channelAction, setChannelAction] = useState<string | null>(null);
    const [broadcastAction, setBroadcastAction] = useState<string | null>(null);

    async function retryFailure(failure: NotificationFailure) {
        if (retryingId) return;
        setRetryingId(failure.id);
        try {
            await apiPost(`/api/admin/notifications/failures/${failure.id}/retry`, {});
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'failures'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'kpis'] })
            ]);
            toast('Notification retry queued', { detail: failure.recipient });
        } catch (error) {
            toast('Retry failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Unable to retry notification')
            });
        } finally {
            setRetryingId(null);
        }
    }

    async function toggleChannel(channelLabel: string, paused?: boolean) {
        const channel = channelLabel.toLowerCase().startsWith('in-app') ? 'in-app' : 'email';
        setChannelAction(channel);
        try {
            if (paused) {
                await resumeNotificationChannel(channel);
                toast('Notification channel resumed', { detail: channelLabel });
            } else {
                const reason = window.prompt(`Pause ${channelLabel}. Reason required:`)?.trim();
                if (!reason) return;
                await pauseNotificationChannel(channel, reason);
                toast('Notification channel paused', { detail: channelLabel });
            }
            await qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'channels'] });
        } catch (error) {
            toast(getHttpErrorMessage(error, 'Unable to update channel'), { kind: 'danger' });
        } finally {
            setChannelAction(null);
        }
    }

    async function runBroadcastAction(broadcast: BroadcastRow, action: 'send' | 'cancel') {
        setBroadcastAction(`${action}:${broadcast.id}`);
        try {
            if (action === 'send') {
                await sendBroadcast(broadcast.id);
                toast('Broadcast sent', { detail: broadcast.title });
            } else {
                await cancelBroadcast(broadcast.id);
                toast('Broadcast cancelled', { detail: broadcast.title });
            }
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'broadcasts'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'kpis'] })
            ]);
        } catch (error) {
            toast(getHttpErrorMessage(error, `Unable to ${action} broadcast`), { kind: 'danger' });
        } finally {
            setBroadcastAction(null);
        }
    }

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Notifications</h1>
                    <p>Delivery across email, in-app, Electron, push, SMS.</p>
                </div>
                <div className="page-actions">
                    <Link className="btn btn-ghost" to="/email-templates">
                        Manage templates
                    </Link>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={() => setBroadcastOpen(true)}
                    >
                        Send broadcast
                    </RoleButton>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        Delivery KPIs and failure rows are live. Managed template draft + publish
                        workflow is now available. Retry and broadcast queue mutations are wired.
                    </span>
                    <span className="meta">partial</span>
                </div>
            </section>

            <section className="kpi-grid kpi-grid-6" aria-label="Notification metrics">
                <KpiMini label="Sent (24h)" value={kpis.data?.sent_24h.toLocaleString() ?? '—'} />
                <KpiMini
                    label="Delivery success"
                    value={kpis.data ? `${kpis.data.success_pct.toFixed(1)}%` : '—'}
                />
                <KpiMini
                    label="Email open rate"
                    value={kpis.data ? `${kpis.data.email_open_rate.toFixed(1)}%` : '—'}
                />
                <KpiMini
                    label="Bounces (24h)"
                    value={
                        kpis.data
                            ? `${kpis.data.bounces_24h} (${kpis.data.bounces_hard} hard / ${kpis.data.bounces_soft} soft)`
                            : '—'
                    }
                    alert={!!kpis.data && kpis.data.bounces_hard > 0}
                />
                <KpiMini
                    label="Unsubscribes (30d)"
                    value={kpis.data?.unsubscribes_30d.toString() ?? '—'}
                />
                <KpiMini
                    label="Follow-ups queued"
                    value={kpis.data?.followups_queued.toString() ?? '—'}
                />
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Channels · 24h</h2>
                    </header>
                    <div className="card-body tight">
                        {channels.data && channels.data.length > 0 ? (
                            channels.data.map((c) => (
                                <div
                                    key={c.channel}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto auto auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {c.channel}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            {c.provider}
                                        </div>
                                    </div>
                                    <span className="mono text-muted">
                                        {c.sent_24h.toLocaleString()}
                                    </span>
                                    <span
                                        className="mono"
                                        style={{
                                            color:
                                                c.success_pct < 98
                                                    ? 'var(--warning)'
                                                    : 'var(--success)'
                                        }}
                                    >
                                        {c.success_pct.toFixed(1)}%
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-ghost"
                                        disabled={channelAction !== null}
                                        onClick={() => void toggleChannel(c.channel, c.paused)}
                                        title={c.paused_reason}
                                    >
                                        {c.paused
                                            ? channelAction
                                                ? 'Resuming…'
                                                : 'Resume'
                                            : channelAction
                                              ? 'Pausing…'
                                              : 'Pause'}
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">No channel delivery data yet</div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Broadcast queue</h2>
                    </header>
                    <div className="card-body tight">
                        {broadcasts.data && broadcasts.data.length > 0 ? (
                            broadcasts.data.map((b) => (
                                <div
                                    key={b.id}
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginBottom: 6
                                        }}
                                    >
                                        <span style={{ color: 'var(--text-primary)', flex: 1 }}>
                                            {b.title}
                                        </span>
                                        <Chip tone={broadcastTone(b.state)} dot>
                                            {b.state}
                                        </Chip>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                        {b.audience}
                                        {b.scheduled_at && <> · scheduled {b.scheduled_at}</>}
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-xs btn-ghost"
                                            disabled={
                                                broadcastAction !== null ||
                                                !['queued', 'paused'].includes(b.state)
                                            }
                                            onClick={() => void runBroadcastAction(b, 'send')}
                                        >
                                            {broadcastAction === `send:${b.id}`
                                                ? 'Sending…'
                                                : 'Send'}
                                        </RoleButton>
                                        <RoleButton
                                            scope="owner_only"
                                            className="btn btn-xs btn-ghost"
                                            disabled={
                                                broadcastAction !== null ||
                                                !['queued', 'paused', 'failed'].includes(b.state)
                                            }
                                            onClick={() => void runBroadcastAction(b, 'cancel')}
                                        >
                                            {broadcastAction === `cancel:${b.id}`
                                                ? 'Cancelling…'
                                                : 'Cancel'}
                                        </RoleButton>
                                    </div>
                                    {b.progress_pct > 0 && b.progress_pct < 100 && (
                                        <div
                                            className="progress-track"
                                            style={{ marginTop: 6, height: 4 }}
                                        >
                                            <div
                                                className="progress-fill"
                                                style={{ width: `${b.progress_pct}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">No queued broadcasts</div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Template performance · 30d</h2>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Template</th>
                                <th>Category</th>
                                <th className="numeric">Sent 30d</th>
                                <th className="numeric">Open rate</th>
                                <th className="numeric">Click rate</th>
                                <th className="numeric">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.data && templates.data.length > 0 ? (
                                templates.data.map((t) => (
                                    <tr
                                        key={t.slug}
                                        aria-selected={activeTemplate?.slug === t.slug}
                                        onClick={() => setActiveTemplate(t)}
                                    >
                                        <td className="mono">{t.slug}</td>
                                        <td>
                                            <Chip tone="muted">{t.category}</Chip>
                                        </td>
                                        <td className="numeric">{t.sent_30d.toLocaleString()}</td>
                                        <td className="numeric">{t.open_rate.toFixed(1)}%</td>
                                        <td className="numeric">{t.click_rate.toFixed(1)}%</td>
                                        <td className="numeric">
                                            <button
                                                className="btn btn-sm btn-ghost"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setActiveTemplate(t);
                                                }}
                                            >
                                                Edit template
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">
                                        No template performance data is available yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Failures & bounces</h2>
                </header>
                <div className="card-body tight">
                    {failures.data && failures.data.length > 0 ? (
                        failures.data.map((f) => (
                            <div
                                key={f.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '70px 120px 1fr auto',
                                    gap: 10,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <span className="mono text-muted">{f.ts}</span>
                                <Chip tone="muted">{f.channel}</Chip>
                                <div>
                                    <div style={{ color: 'var(--text-primary)' }}>
                                        {f.recipient}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                        {f.template} · {f.reason}
                                    </div>
                                </div>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    disabled={retryingId === f.id}
                                    onClick={() => void retryFailure(f)}
                                >
                                    {retryingId === f.id ? 'Retrying…' : 'Retry'}
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">No persisted failures in range</div>
                        </div>
                    )}
                </div>
            </section>

            <TemplateEditorModal
                template={activeTemplate}
                onClose={() => setActiveTemplate(null)}
            />
            <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
        </div>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
